import React, { useRef, useState, useLayoutEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import AnimatedPage from '../components/AnimatedPage'
import { calcInteractions } from '../helpers/functions'
import { gsap } from 'gsap'
import bgClean from '../assets/General/General_Clean.jpg'
import videoTransitionIn from '../assets/outro/Intro.mp4'
import bgStill from '../assets/outro/Still.jpg'

const fadeIn = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      duration: 1,
    },
  },
  exit: {
    opacity: 0,
  },
}

const OutroPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const thisRef = useRef()
  const timeoutRef = useRef()
  const [interactionsWidth, setInteractionsWidth] = useState('0px')
  const [interactionsHeight, setInteractionsHeight] = useState('0px')

  const isPlayAll = location?.state?.isPlayAll
  const prevPageUrl = location?.state?.from || '/'

  useLayoutEffect(() => {
    let isMounted = true
    const sixteenNineAspect = 16 / 9
    const viewportHeight = window.innerHeight
    const viewportWidth = window.innerWidth
    const widthIfConstarainedByHeight = viewportHeight * sixteenNineAspect
    const heightIfConstarainedByWidth = viewportWidth / sixteenNineAspect
    const videoTransitionInElement = thisRef.current.querySelector(
      '.video-transition-in'
    )

    if (widthIfConstarainedByHeight <= viewportWidth) {
      setInteractionsWidth(widthIfConstarainedByHeight + 'px')
      setInteractionsHeight(viewportHeight + 'px')
    } else {
      setInteractionsWidth(viewportWidth + 'px')
      setInteractionsHeight(heightIfConstarainedByWidth + 'px')
    }

    window.addEventListener('resize', calcInteractions)

    if (bgStill !== '') {
      const bgMediaElement = thisRef.current.querySelector('.bg-media')

      const displayStill = () => {
        bgMediaElement.classList.remove('d-none')
      }

      videoTransitionInElement.addEventListener('ended', displayStill)
    }

    const playTransitionIn = async () => {
      try {
        await videoTransitionInElement.play()
      } catch (error) {
        console.log(error)
      }
    }

    // transition in
    const videoTransitionIn = setTimeout(() => {
      if (isMounted) {
        playTransitionIn()
      }
    }, 0)

    // navigation
    const ctx = gsap.context(() => {
      const bg2 = thisRef.current.querySelector('.bg-2')
      const tween = gsap.to(bg2, {
        opacity: 1,
        duration: 0.5,
        paused: true,
      })

      const navTo = (e) => {
        e.stopPropagation()
        const link = e.target
        const navToRoute = link.dataset.route

        tween.play(0, false)
        timeoutRef.current = setTimeout(() => {
          navigate(navToRoute, {
            state: {
              transition: 1,
              isPlayAll,
            },
          })
        }, 500)
      }

      const navBtns = thisRef.current.querySelectorAll('.nav-btn, .action-btn')

      navBtns.forEach((navBtn) => {
        navBtn.addEventListener('click', navTo)
      })

      // Cleanup event listeners
      return () => {
        navBtns.forEach((navBtn) => {
          navBtn.removeEventListener('click', navTo)
        })
      }
    })

    // Unmount -> cleanup
    return () => {
      isMounted = false
      ctx.revert()
      clearTimeout(timeoutRef.current)
      clearTimeout(videoTransitionIn)
      window.removeEventListener('resize', calcInteractions)
    }
  }, [navigate, thisRef, isPlayAll])

  return (
    <AnimatedPage>
      <main
        className='d-flex flex-grow-1 flex-column justify-content-center align-items-center bg-black overflow-hidden'
        ref={thisRef}
      >
        <div className='ratio ratio-16x9 mh-100vh'>
          {/* Displays before Intro Video loads */}
          <img
            src={bgClean}
            style={{ objectFit: 'contain' }}
            alt='background'
          />
          {/* Slide Intro Video */}
          <motion.video
            variants={fadeIn}
            initial='initial'
            animate='animate'
            exit='exit'
            src={videoTransitionIn}
            type='video/mp4'
            className='video-transition-in w-100'
            muted
          />
          {/* Slide Still Image */}
          <img
            src={bgStill}
            className='bg-media d-none w-100'
            style={{ objectFit: 'contain' }}
            alt='background'
          />
          {/* Background to fade to when navigating away */}
          <img
            className='bg-2'
            src={bgClean}
            style={{ objectFit: 'contain', opacity: 0 }}
            alt='background'
          />
          <div
            className='interactions overflow-hidden'
            style={{ width: interactionsWidth, height: interactionsHeight }}
          >
            <div
              data-route={prevPageUrl}
              className='position-absolute nav-btn nav-btn-prev'
            />

            <div
              data-route='/'
              className='position-absolute nav-btn nav-btn-home'
            />
          </div>
        </div>
      </main>
    </AnimatedPage>
  )
}

export default OutroPage
